import { useCallback, useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Copy, Play, Download, KeyRound, Trash2 } from 'lucide-react';

interface PlatformKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  status: 'active' | 'revoked';
}

interface QueryResult {
  columns: string[];
  rows: unknown[][];
  row_count: number;
  truncated: boolean;
  max_rows: number;
  duration_ms: number;
}

interface AuditEntry {
  id: string;
  actor_type: string;
  actor_id?: string | null;
  platform_api_key_id?: string | null;
  surface: string;
  sql: string;
  status: string;
  row_count?: number | null;
  truncated: boolean;
  duration_ms?: number | null;
  error?: string | null;
  created_at?: string | null;
}

const cell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const toCsv = (result: QueryResult): string => {
  const escape = (v: unknown) => {
    const s = cell(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [result.columns.map(escape).join(','), ...result.rows.map((r) => r.map(escape).join(','))].join('\n');
};

export default function PlatformSqlTab() {
  const api = useApi();
  const [keys, setKeys] = useState<PlatformKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [rawKey, setRawKey] = useState<string | null>(null);

  const [sql, setSql] = useState('select id, name, license_type, created_at from organizations order by created_at desc limit 20');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);

  const [audit, setAudit] = useState<AuditEntry[]>([]);

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      const { data } = await api.get('/admin/sql/keys');
      setKeys(data.keys ?? []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load platform keys');
    } finally {
      setKeysLoading(false);
    }
  }, [api]);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/sql/audit?limit=50');
      setAudit(data.entries ?? []);
    } catch {
      /* audit is informational */
    }
  }, [api]);

  useEffect(() => {
    loadKeys();
    loadAudit();
  }, [loadKeys, loadAudit]);

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Name the key after who will hold it (e.g. "Cursor cloud agents").');
      return;
    }
    try {
      const { data } = await api.post('/admin/sql/keys', { name: newKeyName.trim() });
      setRawKey(data.raw_key);
      setNewKeyName('');
      await loadKeys();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create key');
    }
  };

  const revokeKey = async (key: PlatformKey) => {
    if (!window.confirm(`Revoke "${key.name}"? Anything holding it loses prod reads immediately.`)) return;
    try {
      await api.remove(`/admin/sql/keys/${key.id}`);
      await loadKeys();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to revoke key');
    }
  };

  const runQuery = async () => {
    setRunning(true);
    setQueryError(null);
    try {
      const { data } = await api.post('/admin/sql/query', { sql, format: 'json' });
      setResult(data);
    } catch (e: any) {
      setResult(null);
      setQueryError(e?.message || 'Query failed');
    } finally {
      setRunning(false);
      loadAudit();
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const blob = new Blob([toCsv(result)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-sql-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <TabsContent value="platform-sql" className="mt-0 space-y-8">
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4" /> Platform keys</h2>
          <p className="text-sm text-muted-foreground">
            Read-only production access for Aimdoc's own agents. A key authenticates <code>POST /admin/sql/query</code> and,
            as the credential of an MCP server binding, the <code>run_platform_sql</code> tool. Every statement is audited below.
          </p>
        </div>
        <div className="flex gap-2 max-w-xl">
          <Input placeholder='Who holds it, e.g. "Cursor cloud agents"' value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
          <Button onClick={createKey}>Create key</Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-muted-foreground">{keysLoading ? 'Loading…' : 'No keys yet.'}</TableCell></TableRow>
            )}
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell><code>{k.key_prefix}…</code></TableCell>
                <TableCell><Badge variant={k.status === 'active' ? 'default' : 'secondary'}>{k.status}</Badge></TableCell>
                <TableCell>{k.created_at ? formatDate(k.created_at) : ''}</TableCell>
                <TableCell>{k.last_used_at ? formatDate(k.last_used_at) : 'never'}</TableCell>
                <TableCell className="text-right">
                  {k.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={() => revokeKey(k)} title="Revoke"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">SQL console</h2>
          <p className="text-sm text-muted-foreground">
            One read statement, READ ONLY transaction, short timeout, rows capped. Filter by an id and add a LIMIT.
          </p>
        </div>
        <Textarea value={sql} onChange={(e) => setSql(e.target.value)} rows={6} className="font-mono text-sm" />
        <div className="flex items-center gap-2">
          <Button onClick={runQuery} disabled={running}><Play className="h-4 w-4 mr-1" />{running ? 'Running…' : 'Run'}</Button>
          <Button variant="outline" onClick={downloadCsv} disabled={!result}><Download className="h-4 w-4 mr-1" />CSV</Button>
          {result && (
            <span className="text-sm text-muted-foreground">
              {result.row_count} row{result.row_count === 1 ? '' : 's'}{result.truncated ? ` (capped at ${result.max_rows})` : ''} · {result.duration_ms} ms
            </span>
          )}
        </div>
        {queryError && <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{queryError}</div>}
        {result && result.columns.length > 0 && (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>{result.columns.map((c) => <TableHead key={c} className="whitespace-nowrap">{c}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {result.rows.map((r, i) => (
                  <TableRow key={i}>
                    {r.map((v, j) => <TableCell key={j} className="max-w-[28rem] truncate font-mono text-xs" title={cell(v)}>{cell(v)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Recent statements</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Surface</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rows</TableHead>
              <TableHead>SQL</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audit.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">Nothing yet.</TableCell></TableRow>}
            {audit.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="whitespace-nowrap">{a.created_at ? formatDate(a.created_at) : ''}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {a.actor_type === 'platform_key'
                    ? keys.find((k) => k.id === a.platform_api_key_id)?.name ?? 'key'
                    : 'admin'}
                </TableCell>
                <TableCell>{a.surface}</TableCell>
                <TableCell>
                  <Badge variant={a.status === 'ok' ? 'default' : a.status === 'refused' ? 'secondary' : 'destructive'} title={a.error ?? undefined}>{a.status}</Badge>
                </TableCell>
                <TableCell>{a.row_count ?? ''}{a.truncated ? '+' : ''}</TableCell>
                <TableCell className="max-w-[32rem] truncate font-mono text-xs" title={a.sql}>{a.sql}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <Dialog open={rawKey !== null} onOpenChange={(open) => !open && setRawKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy the key now</DialogTitle>
            <DialogDescription>It is shown once. Only its digest is stored.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-muted p-2 text-xs">{rawKey}</code>
            <Button variant="outline" size="sm" onClick={() => rawKey && copy(rawKey)}><Copy className="h-4 w-4" /></Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRawKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
